import * as pdfMake from "pdfmake/build/pdfmake.js";

import DateUtil from "@/plugins/date-util/DateUtil";

/**
 * ### 文書送付、確認・同意ユーザー一覧のPDF操作
 */
export class SendDocumentsOptionTargetOtherUsersPdf {
  dateUtil: DateUtil;
  normalFont: string;

  /**
   * ### コンストラクター
   *
   * - 渡されたフォントをPdfMakeに設定する
   */
  constructor(normalFont: string) {
    this.dateUtil = new DateUtil();
    this.normalFont = normalFont;

    pdfMake.vfs = { "SendDocumentPdf.ttf": this.normalFont };
    pdfMake.fonts = {
      SendDocumentPdf: {
        normal: "SendDocumentPdf.ttf",
        bold: "SendDocumentPdf.ttf"
      }
    };

    return;
  }

  public createAndDownloadPdf(
    items: Array<OptionTargetOtherUserTableItem>,
    facilityName: string,
    title: string,
    sendDocumentDatetime: string,
    type: OptionTargetOtherUserListModalType,
    fileName: string
  ): void {
    const docDefinition: any = {
      styles: this.createStyles(),
      defaultStyle: this.createDefaultStyle(),
      pageSize: "A4",
      pageOrientation: "portrait",
      pageMargins: [27, 14, 27, 27], // [left, top, right, bottom]
      info: this.createInfo(),
      content: this.createContent(items, facilityName, title, sendDocumentDatetime, type),
      footer: this.createFooter()
    };

    pdfMake.createPdf(docDefinition).download(fileName);

    return;
  }

  /**
   * ### PDFのスタイルを作成する
   *
   * - 項目毎、または共通で使用できるスタイルを指定する
   */
  private createStyles(): any {
    return {
      datetime: {
        fontSize: 12,
        alignment: "right",
        margin: [0, 0, 0, 5]
      },
      title: {
        fontSize: 18,
        bold: true,
        alignment: "center",
        margin: [0, 0, 0, 18]
      },
      names: {
        fontSize: 12
      },
      table: {
        fontSize: 10,
        margin: [0, 10, 0, 0]
      },
      tableHeader: {
        bold: true,
        fillColor: "#EEEEEE",
        alignment: "center",
        margin: [2, 2, 2, 2]
      }
    };
  }

  /**
   * ### PDFの共通スタイルを作成する
   *
   * - 全体で共通で使用できるスタイルを指定する
   */
  private createDefaultStyle(): any {
    return {
      font: "SendDocumentPdf"
    };
  }

  /**
   * ### PDF文書情報を作成する
   */
  private createInfo(): any {
    return {
      title: "HitomeQコネクト 文書送付一覧",
      author: "KONICAMINOLA"
    };
  }

  /**
   * ### フッター情報を作成する
   *
   * - ページ番号を印字する
   */
  private createFooter(): Function {
    return (currentPage: number, pageCount: number) => {
      return { text: currentPage.toString() + " / " + pageCount + "ページ", style: { fontSize: 12, alignment: "center", margin: [0, 12, 0, 12] } };
    };
  }

  /**
   * ### 印刷情報を作成する
   *
   * - ヘッダー部
   * - コンテンツ部(文書送付情報)
   */
  private createContent(
    items: Array<OptionTargetOtherUserTableItem>,
    facilityName: string,
    title: string,
    sendDocumentDatetime: string,
    type: OptionTargetOtherUserListModalType
  ): any {
    let contents: Array<any> = [];

    contents = this.createHeader(facilityName, title, sendDocumentDatetime, type);
    contents.push(this.createTable(items, type));

    return contents;
  }

  /**
   * ### コンテンツのタイトル及び固定部分を作成する
   */
  private createHeader(facilityName: string, title: string, sendDocumentDatetime: string, type: OptionTargetOtherUserListModalType): Array<any> {
    const contents: Array<any> = [];

    // 日付
    contents.push({
      text: this.pdfCreateDateTime(),
      style: "datetime"
    });

    // タイトル
    contents.push({
      text: this.createPdfTitle(type),
      style: "title"
    });

    // 施設名、文書タイトル、送信日時
    contents.push({
      text: ["施設名：", { text: `${facilityName}`, bold: true }],
      style: "names"
    });
    contents.push({
      text: ["タイトル：", { text: `${title}`, bold: true }],
      style: "names"
    });
    contents.push({
      text: ["送信日時：", { text: `${sendDocumentDatetime}`, bold: true }],
      style: "names"
    });

    return contents;
  }

  /**
   * ### 作成日時を「yyyy年 mm月 dd日」の形式の文字列にする
   */
  private pdfCreateDateTime(): string {
    const datetime: string = this.dateUtil.convertDateToString(new Date(), "yyyy年 mm月 dd日", false);
    return datetime;
  }

  /**
   * ### PDFタイトルを作成する
   *
   * @param type 種別
   */
  private createPdfTitle(type: OptionTargetOtherUserListModalType): string {
    switch (type) {
      case OptionTargetOtherUserListModalType.AGREEMENT: {
        return "HitomeQコネクト 文書送付　同意者一覧";
      }
      case OptionTargetOtherUserListModalType.CONFIRM: {
        return "HitomeQコネクト 文書送付　確認者一覧";
      }
      default: {
        return "";
      }
    }
  }

  /**
   * ### テーブル情報を作成する
   *
   * @param items 文書送付情報
   */
  private createTable(items: Array<OptionTargetOtherUserTableItem>, type: OptionTargetOtherUserListModalType): any {
    // テーブル
    const tableContent: any = {
      style: "table",
      table: {
        headerRows: 1,
        widths: ["*", "*", 110, "*"]
      }
    };
    const body: Array<any> = [
      [
        { text: "施設関係者グループ", style: "tableHeader" },
        { text: "施設関係者メンバー", style: "tableHeader" },
        { text: this.getReplyDatetimeRowName(type), style: "tableHeader" },
        { text: "返信時の連絡事項", style: "tableHeader" }
      ]
    ];
    for (const item of items) {
      body.push([`${item.name}（${item.groupTypeName}）`, item.memberName, { text: this.convertDateText(item.datetime), style: { alignment: "center" } }, item.message]);
    }
    tableContent.table.body = body;

    return tableContent;
  }

  /**
   * ### 返信日時の列名を返す
   */
  private getReplyDatetimeRowName(type: OptionTargetOtherUserListModalType): string {
    if (type == OptionTargetOtherUserListModalType.AGREEMENT) {
      return "同意の返信日時";
    } else if (type == OptionTargetOtherUserListModalType.CONFIRM) {
      return "同意の確認日時";
    } else {
      return "";
    }
  }

  /**
   * ### 日付を「yyyy年mm月dd日 hh:MM」形式の文字列に変換する
   *
   * @param datetime 日付
   */
  private convertDateText(datetime: number | null): string {
    if (datetime == null) {
      return "ー";
    }

    const date: string = this.dateUtil.convertNumberToString(datetime, "yyyy年mm月dd日 hh:MM");

    return date;
  }
}

/** テーブルの1行あたりの情報 */
export interface OptionTargetOtherUserTableItem {
  /** 施設関係者グループソートキー */
  otherUserGroupsSortKey: string;
  /** グループを一意に識別する ID */
  id: number;
  /** グループ名 */
  name: string;
  /** グループの種類の ID。otherUserGroupTypes の id とひもづく */
  groupTypeId: number;
  /** グループ種類名 */
  groupTypeName: string;
  /** グループ内のメンバーを一意に識別する ID */
  memberId: number;
  /** メンバー名 */
  memberName: string;
  /** メンバー電話番号 */
  memberTel: string;
  /** メンバー所属 */
  memberOrganization: string;
  /** メンバーのLINE の ID */
  memberLineId: string | null;
  /** 返信日時 */
  datetime: number | null;
  /** 返信内容 */
  message: string;
  /** 格納元情報 */
  source: any;
}

export enum OptionTargetOtherUserListModalType {
  UNKNOWN = -1,
  AGREEMENT = 0,
  NON_AGREEMENT = 1,
  CONFIRM = 2,
  NON_CONFIRM = 3
}
